/**
 * AI Service Tester — standalone dev tool page.
 *
 * No auth, no Redux, no axiosInstance. Talks directly to the AI service URL
 * typed in the top bar (default http://localhost:5001).
 *
 * Route: /ai-tester  (added to App.js as an unprotected route)
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  Play, Square, Loader2, RefreshCw, Code2, Terminal, ExternalLink,
} from 'lucide-react';

const DEFAULT_AI_URL = 'http://localhost:5001';

const SERVICES = [
  {
    id: 'attr-execute',
    label: 'Attribute Execute',
    badge: 'SSE',
    description: 'Test all attributes against run samples',
    method: 'POST',
    streaming: true,
    path: '/api/attribute-test/execute-stream',
    fields: [
      { key: 'control_id', label: 'Control ID', type: 'text', placeholder: 'CTL_000022' },
      { key: 'run_id', label: 'Run ID', type: 'text', placeholder: 'uuid of the test run' },
      { key: 'additional_context', label: 'Additional Context', type: 'textarea', placeholder: 'Optional free-text context for the AI…' },
      { key: 'testing_period_start', label: 'Period Start', type: 'text', placeholder: '2024-01-01' },
      { key: 'testing_period_end', label: 'Period End', type: 'text', placeholder: '2024-12-31' },
    ],
  },
  {
    id: 'map-evidence',
    label: 'Map Evidence',
    badge: 'JSON',
    description: 'Map evidence files to samples & attributes',
    method: 'POST',
    streaming: false,
    path: '/api/attribute-test/map-evidence',
    fields: [
      { key: 'control_id', label: 'Control ID', type: 'text', placeholder: 'CTL_000022' },
      { key: 'control_title', label: 'Control Title', type: 'text', placeholder: '' },
      { key: 'control_objective', label: 'Control Objective', type: 'textarea', placeholder: '' },
      { key: 'samples', label: 'Samples', type: 'json', placeholder: '["SAMP_001", "SAMP_002"]' },
      { key: 'attributes', label: 'Attributes', type: 'json', placeholder: '[{"ref":"A","id":"uuid","label":"Approval documented"}]' },
      { key: 'fields', label: 'Fields', type: 'json', placeholder: '[{"field_key":"date","label":"Transaction Date"}]' },
      { key: 'evidence_files', label: 'Evidence Files', type: 'json', placeholder: '[{"id":"uuid","filename":"file.pdf"}]' },
    ],
  },
  {
    id: 'enrich-plan',
    label: 'Enrich Plan',
    badge: 'JSON',
    description: 'AI-enrich test plan attributes with evidence',
    method: 'POST',
    streaming: false,
    path: '/api/attribute-test/enrich-plan',
    fields: [
      { key: 'run_id', label: 'Run ID', type: 'text', placeholder: 'uuid of the test run' },
      { key: 'control_id', label: 'Control ID', type: 'text', placeholder: 'CTL_000022' },
    ],
  },
  {
    id: 'parse-sample',
    label: 'Parse Sample (Stage 2)',
    badge: 'JSON',
    description: 'Test the parser: raw AI text → structured result',
    method: 'POST',
    streaming: false,
    path: '/api/attribute-test/parse-sample',
    fields: [
      { key: 'sample_ref', label: 'Sample Ref', type: 'text', placeholder: 'SAMP_001' },
      { key: 'file_name', label: 'File Name (optional)', type: 'text', placeholder: 'doc.pdf' },
      { key: 'raw_text', label: 'Raw Stage-1 AI Output', type: 'textarea', rows: 12, placeholder: '## ATTRIBUTE A\nStatus: passed\nObservation: …\n\n## ATTRIBUTE B\nStatus: failed\n…' },
      { key: 'attributes', label: 'Attributes', type: 'json', placeholder: '[{"ref":"A","id":"uuid","label":"Approval documented"}]' },
    ],
  },
  {
    id: 'legacy-execute',
    label: 'Legacy Execute',
    badge: 'SSE',
    description: 'Existing single-pass execution pipeline',
    method: 'POST',
    streaming: true,
    path: '/api/test-execution/execute-stream',
    fields: [
      { key: 'control_id', label: 'Control ID', type: 'text', placeholder: 'CTL_000022' },
      { key: 'execution_id', label: 'Execution ID', type: 'text', placeholder: 'uuid' },
      { key: 'additional_context', label: 'Additional Context', type: 'textarea', placeholder: '' },
      { key: 'testing_period_start', label: 'Period Start', type: 'text', placeholder: '2024-01-01' },
      { key: 'testing_period_end', label: 'Period End', type: 'text', placeholder: '2024-12-31' },
      { key: 'auditor_name', label: 'Auditor Name', type: 'text', placeholder: '' },
    ],
  },
  {
    id: 'health',
    label: 'Health Check',
    badge: 'GET',
    description: 'Check AI service health status',
    method: 'GET',
    streaming: false,
    path: '/api/test-execution/health',
    fields: [],
  },
];

/* ── small helpers ─────────────────────────────────────────────────────────── */

function Badge({ label }) {
  const cls = label === 'SSE'
    ? 'bg-amber-100 text-amber-700'
    : label === 'GET'
      ? 'bg-blue-100 text-blue-700'
      : 'bg-green-100 text-green-700';
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cls}`}>{label}</span>
  );
}

function Tab({ label, active, count, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        active ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {label}
      {count != null && count > 0 && (
        <span className="ml-1.5 bg-indigo-100 text-indigo-700 text-xs px-1.5 rounded-full">{count}</span>
      )}
    </button>
  );
}

/* ── main component ─────────────────────────────────────────────────────────── */

export default function AITesterPage() {
  const [serviceUrl, setServiceUrl] = useState(DEFAULT_AI_URL);
  const [selected, setSelected]     = useState(SERVICES[0]);
  const [tab, setTab]               = useState('inputs');
  const [fields, setFields]         = useState({});
  const [response, setResponse]     = useState(null);
  const [running, setRunning]       = useState(false);
  const [prompts, setPrompts]       = useState([]);
  const [promptKey, setPromptKey]   = useState(null);
  const [promptBody, setPromptBody] = useState(null);
  const [promptLoading, setPromptLoading] = useState(false);
  const abortRef  = useRef(null);
  const eventsEnd = useRef(null);

  // Auto-scroll SSE event list
  useEffect(() => {
    eventsEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [response?.events?.length]);

  // Fetch prompt list when switching to that tab
  useEffect(() => {
    if (tab !== 'prompts') return;
    fetchPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, serviceUrl]);

  // Fetch prompt content when a key is selected
  useEffect(() => {
    if (!promptKey) return;
    setPromptLoading(true);
    fetch(`${serviceUrl}/api/tester/prompt?key=${encodeURIComponent(promptKey)}`)
      .then(r => r.json())
      .then(d => setPromptBody(d.content ?? 'No content.'))
      .catch(() => setPromptBody('Error loading prompt — is the AI service running?'))
      .finally(() => setPromptLoading(false));
  }, [promptKey, serviceUrl]);

  const fetchPrompts = () => {
    fetch(`${serviceUrl}/api/tester/prompts`)
      .then(r => r.json())
      .then(d => setPrompts(d.prompts || []))
      .catch(() => setPrompts([]));
  };

  const selectService = (svc) => {
    setSelected(svc);
    setFields({});
    setResponse(null);
    setTab('inputs');
  };

  const setField = (key, val) => setFields(f => ({ ...f, [key]: val }));

  const buildPayload = () => {
    const out = {};
    for (const f of selected.fields) {
      const raw = fields[f.key] ?? '';
      if (!raw) continue;
      if (f.type === 'json') {
        try { out[f.key] = JSON.parse(raw); } catch { out[f.key] = raw; }
      } else {
        out[f.key] = raw;
      }
    }
    return out;
  };

  const handleRun = async () => {
    setRunning(true);
    setTab('response');
    setResponse({ type: selected.streaming ? 'sse' : 'json', events: [], raw: null, error: null });

    const url = `${serviceUrl}${selected.path}`;

    if (selected.method === 'GET') {
      try {
        const r = await fetch(url);
        const d = await r.json();
        setResponse({ type: 'json', events: [], raw: JSON.stringify(d, null, 2), error: r.ok ? null : `HTTP ${r.status}` });
      } catch (e) {
        setResponse({ type: 'json', events: [], raw: null, error: String(e) });
      }
      setRunning(false);
      return;
    }

    const body = JSON.stringify(buildPayload());

    if (!selected.streaming) {
      try {
        const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body });
        const d = await r.json();
        setResponse({ type: 'json', events: [], raw: JSON.stringify(d, null, 2), error: r.ok ? null : `HTTP ${r.status}` });
      } catch (e) {
        setResponse({ type: 'json', events: [], raw: null, error: String(e) });
      }
      setRunning(false);
      return;
    }

    // SSE
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        signal: ctrl.signal,
      });
      if (!r.ok) {
        const txt = await r.text();
        setResponse(p => ({ ...p, error: `HTTP ${r.status}: ${txt}` }));
        setRunning(false);
        return;
      }
      const reader  = r.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data || data === '[DONE]') continue;
          let parsed = data;
          try { parsed = JSON.parse(data); } catch {}
          setResponse(p => ({ ...p, events: [...(p.events || []), { raw: data, parsed }] }));
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        setResponse(p => ({ ...p, error: String(e) }));
      }
    }
    setRunning(false);
    abortRef.current = null;
  };

  const handleCancel = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setRunning(false);
  };

  /* ── layout ─────────────────────────────────────────────────────────────── */

  const inp = 'w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white';

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col text-gray-800">

      {/* Top bar */}
      <div className="bg-indigo-900 text-white px-6 py-3 flex items-center gap-4 shadow-md flex-shrink-0">
        <span className="text-base font-bold tracking-tight">AI Service Tester</span>
        <span className="text-indigo-400 text-xs hidden sm:block">SKEPTICS internal dev tool — not auth-gated</span>
        <div className="ml-auto flex items-center gap-3">
          <label className="text-xs text-indigo-300">AI Service URL</label>
          <input
            value={serviceUrl}
            onChange={e => setServiceUrl(e.target.value)}
            className="text-sm bg-indigo-800 border border-indigo-600 rounded px-3 py-1 text-white w-56 focus:outline-none focus:ring-1 focus:ring-indigo-400"
          />
          <a href="/" className="text-xs text-indigo-300 hover:text-white flex items-center gap-1">
            <ExternalLink className="h-3.5 w-3.5" /> App
          </a>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">

        {/* Service sidebar */}
        <div className="w-56 bg-white border-r border-gray-200 flex flex-col overflow-y-auto flex-shrink-0">
          <div className="px-3 py-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
            Services
          </div>
          {SERVICES.map(svc => (
            <button
              key={svc.id}
              onClick={() => selectService(svc)}
              className={`w-full text-left px-3 py-3 border-b border-gray-50 transition-colors ${
                selected.id === svc.id
                  ? 'bg-indigo-50 border-l-4 border-l-indigo-500'
                  : 'hover:bg-gray-50 border-l-4 border-l-transparent'
              }`}
            >
              <div className="flex items-center gap-2 mb-0.5">
                <Badge label={svc.badge} />
                <span className="text-sm font-medium truncate">{svc.label}</span>
              </div>
              <p className="text-[11px] text-gray-400 leading-tight">{svc.description}</p>
            </button>
          ))}
        </div>

        {/* Main area */}
        <div className="flex-1 flex flex-col overflow-hidden">

          {/* Tab bar + Run/Cancel */}
          <div className="bg-white border-b border-gray-200 px-4 flex items-center gap-1 pt-1 flex-shrink-0">
            <Tab label="Inputs"         active={tab === 'inputs'}  onClick={() => setTab('inputs')} />
            <Tab label="Prompt Preview" active={tab === 'prompts'} onClick={() => setTab('prompts')} />
            <Tab
              label="Response"
              active={tab === 'response'}
              count={response?.events?.length}
              onClick={() => setTab('response')}
            />
            <div className="ml-auto pb-1.5 flex gap-2">
              {running ? (
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium"
                >
                  <Square className="h-3.5 w-3.5" fill="currentColor" /> Cancel
                </button>
              ) : (
                <button
                  onClick={handleRun}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm font-medium"
                >
                  <Play className="h-3.5 w-3.5" fill="currentColor" /> Run
                </button>
              )}
            </div>
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto">

            {/* ── INPUTS tab ── */}
            {tab === 'inputs' && (
              <div className="p-6 max-w-2xl">
                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge label={selected.badge} />
                    <h2 className="text-base font-semibold">{selected.label}</h2>
                  </div>
                  <div className="text-xs text-gray-400 font-mono">
                    {selected.method} {serviceUrl}{selected.path}
                  </div>
                </div>
                {selected.fields.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No inputs required — just click Run.</p>
                ) : (
                  <div className="space-y-4">
                    {selected.fields.map(f => (
                      <div key={f.key}>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">
                          {f.label}
                          {f.type === 'json' && <span className="ml-1 text-gray-400 font-normal">(JSON)</span>}
                        </label>
                        {f.type === 'textarea' || f.type === 'json' ? (
                          <textarea
                            rows={f.rows ?? (f.type === 'json' ? 3 : 4)}
                            value={fields[f.key] || ''}
                            onChange={e => setField(f.key, e.target.value)}
                            placeholder={f.placeholder}
                            className={`${inp} resize-y`}
                          />
                        ) : (
                          <input
                            type="text"
                            value={fields[f.key] || ''}
                            onChange={e => setField(f.key, e.target.value)}
                            placeholder={f.placeholder}
                            className={inp}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── PROMPT PREVIEW tab ── */}
            {tab === 'prompts' && (
              <div className="flex h-full">
                {/* Prompt list */}
                <div className="w-64 border-r border-gray-200 bg-white flex flex-col flex-shrink-0 overflow-y-auto">
                  <div className="px-3 py-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100 flex items-center justify-between">
                    Templates
                    <button
                      onClick={fetchPrompts}
                      className="text-gray-400 hover:text-gray-600"
                      title="Refresh"
                    >
                      <RefreshCw className="h-3 w-3" />
                    </button>
                  </div>
                  {prompts.length === 0 ? (
                    <p className="px-3 py-4 text-xs text-gray-400 italic">
                      No prompts found — ensure the AI service is running at {serviceUrl}.
                    </p>
                  ) : (
                    prompts.map(p => (
                      <button
                        key={p.key}
                        onClick={() => setPromptKey(p.key)}
                        className={`w-full text-left px-3 py-2 border-b border-gray-50 text-xs transition-colors ${
                          promptKey === p.key ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <div className="font-semibold truncate">{p.name}</div>
                        <div className="text-gray-400 truncate font-mono">{p.key}</div>
                      </button>
                    ))
                  )}
                </div>

                {/* Prompt content */}
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                  {!promptKey ? (
                    <p className="text-sm text-gray-400 italic mt-10 text-center">Select a template from the list.</p>
                  ) : promptLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-10 justify-center">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                  ) : (
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono bg-white border border-gray-200 rounded p-4 overflow-auto min-h-32">
                      {promptBody}
                    </pre>
                  )}
                </div>
              </div>
            )}

            {/* ── RESPONSE tab ── */}
            {tab === 'response' && (
              <div className="p-4">
                {!response ? (
                  <p className="text-sm text-gray-400 italic mt-10 text-center">Run a service to see its response here.</p>
                ) : response.error ? (
                  <div className="bg-red-50 border border-red-200 rounded p-4 max-w-3xl">
                    <div className="text-sm font-semibold text-red-700 mb-2">Error</div>
                    <pre className="text-xs text-red-600 whitespace-pre-wrap font-mono">{response.error}</pre>
                  </div>
                ) : response.type === 'json' ? (
                  <div className="max-w-3xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Code2 className="h-4 w-4 text-gray-400" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">JSON Response</span>
                    </div>
                    <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono bg-white border border-gray-200 rounded p-4 overflow-auto max-h-[70vh]">
                      {response.raw || 'No response data.'}
                    </pre>
                  </div>
                ) : (
                  /* SSE event stream */
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <Terminal className="h-4 w-4 text-gray-400" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">SSE Event Stream</span>
                      <span className="text-xs text-gray-400">
                        {response.events.length} event{response.events.length !== 1 ? 's' : ''}
                        {running ? ' · live…' : ' · complete'}
                      </span>
                    </div>
                    <div className="space-y-1 max-h-[70vh] overflow-y-auto">
                      {response.events.length === 0 && running && (
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <Loader2 className="h-4 w-4 animate-spin" /> Waiting for events…
                        </div>
                      )}
                      {response.events.map((ev, i) => {
                        const p = ev.parsed;
                        const isObj = p !== null && typeof p === 'object';
                        const stage = isObj ? (p.stage || p.type || p.event || '') : '';
                        const msg   = isObj ? (p.message || p.msg || '') : '';
                        const pct   = isObj && p.progress != null ? p.progress : null;
                        const isErr = isObj && (p.error || stage === 'error' || stage === 'failed');
                        const isDone = isObj && (stage === 'complete' || stage === 'done' || stage === 'completed');
                        const extraKeys = isObj
                          ? Object.keys(p).filter(k => !['stage','type','event','message','msg','progress'].includes(k))
                          : [];

                        return (
                          <div
                            key={i}
                            className={`rounded px-3 py-2 text-xs font-mono border ${
                              isErr  ? 'bg-red-50 border-red-200 text-red-700' :
                              isDone ? 'bg-green-50 border-green-200 text-green-700' :
                                       'bg-white border-gray-100 text-gray-700'
                            }`}
                          >
                            <div className="flex items-start gap-2 flex-wrap">
                              <span className="text-gray-300 select-none flex-shrink-0 tabular-nums">
                                {String(i + 1).padStart(3, '0')}
                              </span>
                              {stage && (
                                <span className={`font-semibold flex-shrink-0 ${
                                  isErr ? 'text-red-600' : isDone ? 'text-green-600' : 'text-indigo-600'
                                }`}>
                                  [{stage}]
                                </span>
                              )}
                              {pct != null && (
                                <span className="text-gray-400 flex-shrink-0">{pct}%</span>
                              )}
                              {msg ? (
                                <span className="flex-1 break-all">{msg}</span>
                              ) : !stage ? (
                                <span className="flex-1 break-all text-gray-500">{ev.raw}</span>
                              ) : null}
                            </div>
                            {extraKeys.length > 0 && (
                              <details className="mt-1 ml-8">
                                <summary className="text-gray-400 cursor-pointer hover:text-gray-600 select-none">
                                  {extraKeys.length} more field{extraKeys.length !== 1 ? 's' : ''}
                                </summary>
                                <pre className="mt-1 text-gray-500 whitespace-pre-wrap overflow-auto">
                                  {JSON.stringify(
                                    Object.fromEntries(extraKeys.map(k => [k, p[k]])),
                                    null, 2
                                  )}
                                </pre>
                              </details>
                            )}
                          </div>
                        );
                      })}
                      <div ref={eventsEnd} />
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
